import React, { useState, useEffect } from 'react';
import { Card, Table, Form, Alert, Spinner, Row, Col, Badge } from 'react-bootstrap';
import AdminToolsLayout from './AdminToolsLayout';

/**
 * Error Log Viewer Component
 * Displays recent error log entries with severity filtering
 */
const ErrorLogViewer = () => {
  const [errors, setErrors] = useState([]);
  const [severity, setSeverity] = useState('');
  const [limit, setLimit] = useState(100);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchErrorLog();
  }, [severity, limit]);

  const fetchErrorLog = async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (severity) params.append('severity', severity);
      params.append('limit', limit);

      const response = await fetch(
        `/api/admin/tools/error-log?${params.toString()}`,
        { credentials: 'include' }
      );
      const data = await response.json();

      if (data.error) {
        setError(data.error);
      } else {
        setErrors(data.errors || []);
      }
    } catch (err) {
      setError('Failed to fetch error log');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getSeverityBadge = (sev) => {
    switch (sev) {
      case 'critical':
        return <Badge bg="danger">Critical</Badge>;
      case 'error':
        return <Badge bg="warning">Error</Badge>;
      case 'warning':
        return <Badge bg="info">Warning</Badge>;
      default:
        return <Badge bg="secondary">{sev}</Badge>;
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  return (
    <AdminToolsLayout>
      <Card>
        <Card.Header>
          <h5 className="mb-0">Error Log</h5>
        </Card.Header>
        <Card.Body>
          {/* Filters */}
          <Row className="mb-4">
            <Col md={4}>
              <Form.Group>
                <Form.Label>Severity</Form.Label>
                <Form.Select
                  value={severity}
                  onChange={(e) => setSeverity(e.target.value)}
                >
                  <option value="">Errors &amp; warnings (default)</option>
                  <option value="critical">Critical</option>
                  <option value="error">Error</option>
                  <option value="warning">Warning</option>
                  <option value="info">Info (runs, logins, price updates)</option>
                  <option value="all">All (incl. info)</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Limit</Form.Label>
                <Form.Select
                  value={limit}
                  onChange={(e) => setLimit(parseInt(e.target.value))}
                >
                  <option value="50">Last 50</option>
                  <option value="100">Last 100</option>
                  <option value="200">Last 200</option>
                  <option value="500">Last 500</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          {error && <Alert variant="danger">{error}</Alert>}

          {loading ? (
            <div className="text-center py-4"><Spinner animation="border" /></div>
          ) : errors.length === 0 ? (
            <p className="text-muted">No errors found.</p>
          ) : (
            <>
              <p className="text-muted small mb-3">
                Showing {errors.length} entries
              </p>
              <div style={{ overflowX: 'auto' }}>
                <Table striped bordered hover responsive>
                  <thead>
                    <tr>
                      <th>Timestamp</th>
                      <th>Severity</th>
                      <th>Step</th>
                      <th>Provider</th>
                      <th>Model</th>
                      <th>Subject</th>
                      <th>Message</th>
                    </tr>
                  </thead>
                  <tbody>
                    {errors.map((err, idx) => (
                      <tr key={err.id || idx}>
                        <td style={{ whiteSpace: 'nowrap', fontSize: '0.85rem' }}>
                          {formatDate(err.created_at)}
                        </td>
                        <td>{getSeverityBadge(err.severity)}</td>
                        <td>{err.step || 'N/A'}</td>
                        <td>{err.provider || 'N/A'}</td>
                        <td>{err.model_used || 'N/A'}</td>
                        <td>{err.subject || 'N/A'}</td>
                        <td style={{ fontSize: '0.85rem' }}>
                          {err.message || 'N/A'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </>
          )}
        </Card.Body>
      </Card>
    </AdminToolsLayout>
  );
};

export default ErrorLogViewer;
